import { useState, useEffect } from 'react';
import {
    getBrands,
    getGroups,
    getTicketForms,
    getZendeskUser,
    getTicketFormById,
    getTicketFields,
    type ZendeskItem,
    type TicketField,
    zafClient
} from '@/services/zendesk';
import { SELECTED_ORDER_ID_KEY } from "@/context";

export function useFormulario() {
    const [loading, setLoading] = useState(true);
    const [ticketId, setTicketId] = useState<number | null>(null);
    const [orderId, setOrderId] = useState<string | null>(null);

    const [activeFormFields, setActiveFormFields] = useState<TicketField[]>([]);
    const [customFields, setCustomFields] = useState<Record<number, string | number | boolean>>({});
    const [priority, setPriority] = useState<string>('');

    const [raw, setRaw] = useState({
        brands: [] as ZendeskItem[],
        groups: [] as ZendeskItem[],
        forms: [] as ZendeskItem[],
        users: [] as ZendeskItem[],
        fields: [] as TicketField[]
    });

    const [selectedBrand, setSelectedBrand] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedForm, setSelectedForm] = useState('');
    const [selectedRequester, setSelectedRequester] = useState('');
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (!zafClient) return;
        const handleIdUpdate = (data: unknown) => {
            if (data) setTicketId(Number(data));
        };
        zafClient.on('ticket_id_updated', handleIdUpdate);
        return () => { zafClient?.off('ticket_id_updated', handleIdUpdate); };
    }, []);

    useEffect(() => {
        async function fetchFormFields() {
            if (!selectedForm) {
                setActiveFormFields([]);
                setCustomFields({});
                return;
            }

            const formObj = raw.forms.find(f => f.name === selectedForm);
            if (formObj?.id) {
                try {
                    const data = (await getTicketFormById(formObj.id)) as { ticket_form: { ticket_field_ids: number[] } };
                    if (data && data.ticket_form) {
                        const fieldIds = data.ticket_form.ticket_field_ids || [];

                        const matchedFields = fieldIds
                            .map(id => raw.fields.find(f => f.id === id))
                            .filter((f): f is TicketField => f !== undefined);

                        setActiveFormFields(matchedFields);
                        setCustomFields({});
                    }
                } catch (error) {
                    console.error("Erro ao buscar campos do formulário:", error);
                    setActiveFormFields([]);
                }
            }
        }
        fetchFormFields();
    }, [selectedForm, raw.forms, raw.fields]);

    useEffect(() => {
        async function loadData() {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const idFromUrl = urlParams.get('parent_ticket_id');
                const idFromStorage = localStorage.getItem('CURRENT_TICKET_ID');
                setTicketId(idFromUrl ? Number(idFromUrl) : (idFromStorage ? Number(idFromStorage) : null));

                const storedOrderId = localStorage.getItem(SELECTED_ORDER_ID_KEY);
                if (storedOrderId) {
                    setSubject(`Pedido: #${storedOrderId}`);
                    setOrderId(storedOrderId);
                }

                const [brands, groups, forms, users, fields] = await Promise.all([
                    getBrands().catch(() => ({ brands: [] as ZendeskItem[] })),
                    getGroups().catch(() => ({ groups: [] as ZendeskItem[] })),
                    getTicketForms().catch(() => ({ ticket_forms: [] as ZendeskItem[] })),
                    getZendeskUser().catch(() => ({ users: [] as ZendeskItem[] })),
                    getTicketFields().catch(() => ({ ticket_fields: [] as TicketField[] }))
                ]);

                setRaw({
                    brands: (brands.brands || []).filter((b: ZendeskItem) => b.active),
                    groups: groups.groups || [],
                    forms: (forms.ticket_forms || []).filter((f: ZendeskItem & { active?: boolean }) => f.active),
                    users: users.users || [],
                    fields: fields.ticket_fields || []
                });
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, []);

    return {
        ticketId,
        orderId,
        activeFormFields,
        customFields, setCustomFields,
        priority, setPriority,
        options: {
            brands: raw.brands.map(b => b.name),
            groups: raw.groups.map(g => g.name),
            forms: raw.forms.map(f => f.name),
            requesters: raw.users.map(u => u.name)
        },
        raw,
        loading,
        subject, setSubject,
        description, setDescription,
        selectedBrand, setSelectedBrand,
        selectedGroup, setSelectedGroup,
        selectedForm, setSelectedForm,
        selectedRequester, setSelectedRequester
    };
}